import React, { useState, useMemo, useEffect } from 'react';
import HeaderUploader from './headerMatching';
import HeaderMapper from './headerMapper';
import { canonicalizeFileStreaming } from '../utils/canonicalizeFileStreaming';
import {  autoDetectMapping, SCHEMA_PRESETS } from '../utils/schemaPresets';
import { getRequiredFor, PLATFORM_SPECS, getOptionalFor } from '../utils/platformSpecs';
import SignOutButton from './SignOutButton';
import PastSubmissionsModal from './PastSubmissionsModal';
import { useAuth } from '../authContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAllPresets } from '../hooks/useAllPresets';
import { validateSenderDistincts } from '../utils/validate-deeper';
import RequestApi from "./RequestApi";
import RequestApiKey from "../pages/RequestApiKey";
import { useNavigate } from "react-router-dom";

export default function PipelineForm() {
  // -------- existing state ----------
  const [file, setFile] = useState(null);
  const [stem, setStem] = useState('');
  const [pullDate, setPullDate] = useState('');
  const [platform, setPlatform] = useState('');
  const [minSizeDay, setMinSizeDay] = useState(5);
  const [minUserDay, setMinUserDay] = useState(10);
  const [percentile, setPercentile] = useState(98);
  const [clusterThreshold, setClusterThreshold] = useState(0.0);
  const [submitted, setSubmitted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stemError, setStemError] = useState('');
  const [pullDateError, setPullDateError] = useState('');
  const [fileError, setFileError] = useState('');
  const [platformError, setPlatformError] = useState('');
  const [progress, setProgress] = useState(null);
  const [userfile, setUserfile] = useState(null);
  const [valueMaps, setValueMaps] = useState({});
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [analysisMode, setAnalysisMode] = useState(null);
  const isDeeper = analysisMode === 'deeper';

  const [submissionsRefreshTrigger, setSubmissionsRefreshTrigger] = useState(0);
  const [resetUploaderKey, setResetUploaderKey] = useState(0);
  const [submittedData, setSubmittedData] = useState(null);

  const navigate = useNavigate();


  const [idToken, setIdToken] = useState(null);

  // 👇 Decode ID token payload to extract custom:isAdmin
  const isAdmin = useMemo(() => {
    if (!idToken) return false;
    try {
      const payload = JSON.parse(atob(idToken.split(".")[1]));
      return payload["custom:isAdmin"] === "true";
    } catch {
      return false;
    }
  }, [idToken]);
  const { user } = useAuth();
  const userWithAdmin = { ...user, isAdmin };


  // fetch a fresh ID token when user changes or when component mounts
  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        setIdToken(session.tokens?.idToken?.toString() || null);
      } catch (e) {
        console.warn('No ID token (not signed in yet?)', e);
        setIdToken(null);
      }
    })();
  }, [user]);


  // merge DB (global+user) with your existing hardcoded presets
  const { presets: ALL_PRESETS } =
  useAllPresets(SCHEMA_PRESETS);

 // schema + mapping (kept, but no auto-opening of mapper for presets)
  const [headerPreview, setHeaderPreview] = useState(null);
  const [mapping, setMapping] = useState(null);   // src -> canon
  const [presetId, setPresetId] = useState('');   // '' | 'manual' | preset.id
  const [presetName, setPresetName] = useState('');   // '' | 'manual' | preset.id

  const filteredPresets = useMemo(
    () => ALL_PRESETS.filter(p => p.platform === platform),
    [ALL_PRESETS, platform]
  );
  const selectedPreset = useMemo(
    () => filteredPresets.find(p => p.id === presetId) || null,
    [filteredPresets, presetId]
  );

  // Helper: for ZIP, compute the lists to display without doing validation banners
  const computeZipPresetHints = (preset, requiredCanon = []) => {
    const mapSrcToCanon = preset?.mappingSrcToCanon || {};
    const expected = Object.keys(mapSrcToCanon);

    // For "must-have", show the *source* header that maps to each required canonical field.
    // If a required canon isn't in the preset mapping, fall back to showing the canon name.
    const mustHave = requiredCanon.map((canon) => {
      const hit = Object.entries(mapSrcToCanon).find(([, c]) => c === canon);
      return hit ? hit[0] : canon;
    });

    return { expected, mustHave };
  };


  const requiredCanon = useMemo(() => {
    if (selectedPreset?.requiredCanon?.length) return selectedPreset.requiredCanon;
    return getRequiredFor(platform);
  }, [platform, selectedPreset]);

  const optionalCanon = useMemo(() => {
    if (selectedPreset?.optionalCanon?.length) return selectedPreset.optionalCanon;
    return getOptionalFor(platform);
  }, [platform, selectedPreset]);

  // success state: a preset is selected and mapping covers all required canon fields

  const API_BASE = process.env.REACT_APP_API_BASE;
  const THRESHOLD = 1500;

  function InfoTooltip({ text }) {
    return (
      <span className="relative group inline-block ml-1">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 11-0 20 10 10 0 010-20z"/>
        </svg>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
          {text}
        </div>
      </span>
    );
  }

  const isZip = (f) => !!f && (f.name.split('.').pop() || '').toLowerCase() === 'zip';
  const isMappedFile = (f) => !!f && /_mapped\.csv$/i.test(f.name);

  const mappingCoversRequired = useMemo(() => {
    if (!mapping) return false;
    const chosenCanon = new Set(Object.values(mapping));
    return requiredCanon.every(c => chosenCanon.has(c));
  }, [mapping, requiredCanon]);

    // which canon fields the user has actually selected (from mapping)
  // Canon fields the user has actually mapped so far
// All "expected" source headers for the selected preset (left-hand keys)
  const presetExpectedSources = useMemo(() => {
    const m = selectedPreset?.mappingSrcToCanon;
    if (!m || typeof m !== 'object') return [];
    return Array.from(new Set(Object.keys(m)));
  }, [selectedPreset]);

  // Subset of expected sources that correspond to required canon fields (must-have)
  const presetRequiredSources = useMemo(() => {
    const m = selectedPreset?.mappingSrcToCanon;
    if (!m || typeof m !== 'object') return [];
    const required = new Set(requiredCanon);
    return Object.entries(m)
      .filter(([, canon]) => required.has(canon))
      .map(([src]) => src);
  }, [selectedPreset, requiredCanon]);

  // From the preset: left-hand "expected" headers, and those that map to required canon
 const isPresetMappedOK = useMemo(() => {
    return !!selectedPreset && presetId !== 'manual' && !!mapping && mappingCoversRequired;
  }, [selectedPreset, presetId, mapping, mappingCoversRequired]);

  const mustMap = !!file && !isZip(file) && !isMappedFile(file);
  const canSubmit = !!file && (!mustMap || mappingCoversRequired) ;


  // Derive canon -> src (easier for Papermill params like CreatedTime <- "<user column>")
  const canonToSrc = useMemo(() => {
    if (!mapping) return {};
    const out = {};
    // last write wins if multiple src map to same canon (shouldn't happen if you validate)
    for (const [src, canon] of Object.entries(mapping)) {
      if (canon) out[canon] = src;
    }
    return out;
  }, [mapping]);

  // ---------- Preset apply ----------
  const applyPresetIfPossible = async (preset, theFile, preview) => {
    const needs = preset.requiredCanon && preset.platform === platform
      ? preset.requiredCanon
      : requiredCanon;
    const srcToCanon = autoDetectMapping(preset, preview.headers);
    const chosenCanon = new Set(Object.values(srcToCanon));
    const ok = needs.every(c => chosenCanon.has(c));

    if (!ok) {
      // Do NOT open manual mapper automatically; show a clear error instead.
      setMapping(srcToCanon);
      setFileError('Preset could not auto-map all required fields. Choose “No preset — map manually” to complete mapping.');
      return false;
    }

    try {
      setLoading(true);
      setProgress({ phase: 'validate', rows: 0 });

      // Only validate CreatedTime; don't enforce other required headers.
      // (Pass [] so enforceRequired() is effectively skipped.)
      const controller = new AbortController();
      await canonicalizeFileStreaming(
        theFile,
        srcToCanon,
        [],                       // <-- no requiredCanon enforcement
        preset.valueMaps,         // fine to pass; unused if you disabled Pass B
        { onProgress: (p) => setProgress(p), signal: controller.signal }
      );

      setProgress(null);
      setMapping(srcToCanon);
      setFileError('');
      return true;
    } catch (err) {
      setFileError(
        err?.message ||
        'Failed to validate CreatedTime. Choose “No preset — map manually” to continue.'
      );
      return false;
    } finally {
      setLoading(false);
    }

  };

  // ---------------- SUBMIT ----------------

const handleSubmit = async (e, _isDeeperIgnored = false) => {
  e.preventDefault();

  // must choose analysis type first
  if (!analysisMode) {
    setFileError('Please select the type of analysis at the top(FIND analysis or FIND deeper analysis).');
    return;
  }

  // ---- existing validations (unchanged) ----
  if (!stem.trim()) { setStemError('Stem is required'); return; }
  else if (!/^[A-Za-z0-9._-]+$/.test(stem)) {
    setStemError('Only letters, numbers, dots, hyphens, and underscores allowed');
    return;
  } else setStemError('');

  if (!pullDate.trim()) { setPullDateError('Pull date is required'); return; }
  else if (!/^[A-Za-z0-9][A-Za-z0-9_/-]*$/.test(pullDate)) {
    setPullDateError('Pull date must start with a letter or number, and only contain letters, numbers, "-", "_" or "/"');
    return;
  } else setPullDateError('');

  if (!file) { setFileError('Please choose a file'); return; }

  const MAX_MB = 1000;
  if (file.size > MAX_MB * 1024 * 1024) {
    setFileError(`File size must be less than ${MAX_MB} MB`);
    return;
  }
  setFileError('');

  if (!isZip(file) && !isMappedFile(file) && !mappingCoversRequired) {
    setFileError('Please map all required fields before submitting');
    return;
  }

  if (!platform) { setPlatformError('Please select a platform'); return; }
  else setPlatformError('');


  const isDeeper = analysisMode === 'deeper';
  const targetPlatform = isDeeper ? `${platform}-deeper` : platform;


  setLoadingMessage(isDeeper ? 'Validating your file…' : 'Preparing upload…');
  setLoading(true);

  try {
    const lowerName = (file.name || '').toLowerCase();
    const contentType =
      lowerName.endsWith('.csv')  ? 'text/csv' :
      lowerName.endsWith('.xlsx') ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
      lowerName.endsWith('.xls')  ? 'application/vnd.ms-excel' :
      (file.type || 'application/octet-stream');

    // 🔍 NEW: browser-side sender validation only for deeper flow
    if (isDeeper) {
      setFileError('Validating sender counts…');

      const result = await validateSenderDistincts(file, canonToSrc, THRESHOLD);

      if (!result.ok) {
        setFileError(result.reason || 'File rejected by sender validator.');
        setLoading(false);
        return;
      }

      // success – clear validation message
      setFileError('');
      setLoadingMessage('File validated. Uploading file…');
    } else {
      setLoadingMessage('Uploading file…');
    }

    // 🔁 Unified upload flow for both normal + deeper
    const u1 = await fetch(`${API_BASE}/get-upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        originalName: file.name,
        contentType,
        stem,
        pull_date: pullDate,
        platform: targetPlatform,
      }),
    }).then(r => r.json());

    if (!u1.uploadUrl || !u1.key || !u1.jobId) {
      throw new Error('Failed to get upload URL');
    }

    const putResp = await fetch(u1.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!putResp.ok) throw new Error('Upload to S3 failed');
    // console.log(presetName);
    const startResp = await fetch(`${API_BASE}/start-task`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        jobId: u1.jobId,
        key: u1.key,
        stem,
        pull_date: pullDate,
        platform: targetPlatform,
        analysis_mode: analysisMode,
        email: user.email,
        name: user.name,
        min_size_day: minSizeDay,
        min_user_day: minUserDay,
        percentile,
        CLUSTER_THRESHOLD: clusterThreshold,
        src_to_canon: mapping || null,
        canon_to_src: Object.keys(canonToSrc).length ? canonToSrc : null,
        value_maps: Object.keys(valueMaps).length ? valueMaps : null,
        original_file_name: file.name || null,
        account_type: 'Web-App USER',
        // schema_name: presetId || null,
        schema_name: presetName || null,
        // schema_label: presetName || null,
      }),
    });

    if (!startResp.ok) {
      // optional: read error body for debugging
      let errorText = '';
      try {
        errorText = await startResp.text();
      } catch (_) {}

      if (startResp.status === 401 || startResp.status === 403) {
        throw new Error('Session expired. Please sign in again and resubmit.');
      }

      throw new Error(
        `start-task failed with status ${startResp.status} ${errorText || ''}`
      );
    }

    // success only if we reach here
    setSubmitted(true);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 8000);

    setSubmittedData({
      analysisMode,
      stem,
      pullDate,
      platform: targetPlatform,
      minSizeDay,
      minUserDay,
      percentile,
      clusterThreshold
    });


    resetForm();
    setSubmissionsRefreshTrigger(prev => prev + 1);
    setResetUploaderKey(prev => prev + 1);

  } catch (err) {
    console.error(err);
    alert(`Error: ${err.message}`);
  } finally {
    setLoading(false);
    setLoadingMessage('');
  }
};

function resetForm() {
  setFile(null);
  setHeaderPreview(null);
  setMapping(null);
  setPlatform('');
  setPresetId('');
  setPresetName('');
  setStem('');
  setPullDate('');
  setPlatformError('');
  setStemError('');
  setPullDateError('');
  setFileError('');
  setAnalysisMode(null);
  setValueMaps({});
  setMinSizeDay(5);
  setMinUserDay(10);
  setPercentile(98);
  setClusterThreshold(0.0);
}

const submittedIsDeeper = submittedData?.analysisMode === 'deeper';


  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-4xl border border-gray-200">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-30 h-auto rounded-lg mb-4">
            <img src="infauxtech_logo_rectangle.png" alt="Logo" className="w-30 h-20 object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-purple-900 mb-2">Foreign Influence Network Detector (FIND)</h1>
          <p className="text-gray-600">Let's get your Network Detected</p>

          <div className="w-16 h-0.5 bg-orange-500 mx-auto mt-3" />
        </div>

        <div className="space-y-8">
          {/* User Info (refined) */}
          <div className="rounded-xl border border-gray-200 bg-white/90 shadow-sm">
            {/* top accent */}
            <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-purple-500 to-orange-500 rounded-t-xl" />

            <div className="p-5 md:p-6">
              {/* header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  {/* tiny avatar */}
                  <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-sm font-medium">
                    {(user?.name?.[0] || 'U').toUpperCase()}
                  </div>
                  <div className="flex flex-col relative group">
                    <button
                      type="button"
                      className="text-sm text-gray-900 text-left hover:text-gray-700 focus:outline-none"
                    >
                      My Profile
                    </button>

                    <div className="absolute left-0 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-md p-3 text-xs text-gray-700 hidden group-hover:block">
                      <div className="flex flex-col">
                        <span className="font-medium">{user.email}</span>
                        <span className="text-gray-600">{user.name}</span>
                      </div>
                    </div>
                  </div>

                </div>

                {/* ADMIN DASHBOARD LINK — only admins see this */}
                {userWithAdmin?.isAdmin && (
                <button
                  onClick={() => window.location.href = "/admin"}
                  className="text-sm font-semibold text-purple-700 hover:text-purple-900 underline mr-4"
                >
                  View FIND Accounts Dashboard
                </button>
              )}


                <div className="sm:shrink-0 self-start sm:self-auto flex items-center gap-3">
                  {/* XXX Realtime is disabled on the InfauxTech FIND instance. */}
                  {/* <button
                    type="button"
                    onClick={() => navigate("/realtime")}
                    style={{
                      background: "#6b21a8",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    Realtime Dashboards
                  </button> */}

                  <button
                    type="button"
                    onClick={() => navigate("/public")}
                    style={{
                      background: "#6b21a8",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                    title="View reports that users marked as public"
                  >
                    Public Reports
                  </button>

                  <RequestApi onClick={() => setShowRequestModal(true)} />
                  <SignOutButton />
                </div>
              </div>
              <PastSubmissionsModal
                idToken={idToken}
                triggerRefresh={submissionsRefreshTrigger}
              />

              {showRequestModal && (
                <div
                  role="dialog"
                  aria-modal="true"
                  className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                  onClick={() => setShowRequestModal(false)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white rounded-lg p-6 w-[min(900px,96%)] max-h-[90vh] overflow-y-auto shadow-lg"
                  >
                    {/* Close button */}
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={() => setShowRequestModal(false)}
                        aria-label="Close"
                        className="text-gray-600 text-lg hover:text-gray-800"
                      >
                        ×
                      </button>
                    </div>

                    {/* Modal content */}
                    <RequestApiKey />
                  </div>
                </div>
              )}

            </div>
          </div>


              <div className="mt-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-1">
                  New submission
                </h2>
                <p className="text-xs text-gray-600 mb-3">
                  Choose the type of analysis you want to run before uploading your data.
                </p>

                <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-1 text-xs items-center">

                  <button
                    type="button"
                    onClick={() => {
                      setAnalysisMode('normal');
                      setFileError('');
                    }}
                    className={
                      `px-3 py-2 rounded-md transition-colors ` +
                      (analysisMode === 'normal'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-transparent text-gray-700 hover:bg-gray-100')
                    }
                  >
                    FIND analysis
                  </button>

                  {/* Vertical separator */}
                  <div className="w-px h-5 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => {
                      setAnalysisMode('deeper');
                      setFileError('');
                      setPlatform(prev => prev === 'x'|| prev === 'youtube-vid-text' || prev === 'facebook' ? prev : '');
                      setPresetId('');
                      setPresetName('');
                      setMapping(null);
                    }}
                    className={
                      `px-3 py-2 rounded-md transition-colors ` +
                      (analysisMode === 'deeper'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-transparent text-gray-700 hover:bg-gray-100')
                    }
                  >
                    FIND deeper analysis
                  </button>
                </div>


                {isDeeper && (
                  <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-[13px] text-yellow-700">
                    For deeper analysis, the distinct usernames / SenderScreenName / author names in the
                    provided file must <span className="font-semibold">not</span> exceed <span className="font-semibold">1500</span>, otherwise
                    the request will not be processed.
                  </div>
                )}
              </div>


          {/* Upload + Preview */}
          <HeaderUploader
            key={resetUploaderKey}
            onFileSelected={(f) => {
              setFile(f);
              setUserfile(f);
              setMapping(null);
              setHeaderPreview(null);
              setPlatform('');
              setPresetId('');
              setPresetName('');   // keep default “select a schema”
              setFileError('');
            }}
            onPreview={(p) => setHeaderPreview(p)}
            onError={(msg) => setFileError(msg)}
            onBusyChange={(busy) => setLoading(busy)}
          />

          {/* Quick platform selector for ZIP uploads (no preview) */}
          {isZip(file) && (
            <div className="mt-4">
              <label className="block text-xs text-gray-700 mb-1.5">Platform</label>
              <div className="relative mb-4">
                <select
                  value={platform}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPlatform(next);
                    setPresetId('');
                    setPresetName('');
                    setMapping(null);
                    setValueMaps({});
                    setFileError('');
                  }}
                  className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-orange-500"
                >
                  <option value="" disabled>— select a platform —</option>
                  {isDeeper ? (
                    <>
                      <option value="x">X (Twitter)</option>
                      <option value="youtube-vid-text">Youtube Videos Texts</option>
                      </>) : (
                        <>
                          <option value="x">X (Twitter)</option>
                          <option value="reddit">Reddit</option>
                          <option value="steam">Steam</option>
                          <option value="youtube-vid-text">Youtube Videos Texts</option>
                          <option value="youtube-comments">Youtube Comments</option>
                          <option value="disqus">Disqus</option>
                          <option value="bluesky">Bluesky</option>
                        </>
                      )}
                </select>
              </div>

              {/* NEW: schema preset for ZIP (no manual option, no mapping step) */}
              {platform && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-700 mb-1.5">Schema preset</label>
                  <div className="relative">
                    <select
                      value={presetId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setPresetId(id);
                        setFileError('');
                        // no header preview for ZIP; just stash value maps + schema name
                        const preset = filteredPresets.find(p => p.id === id) || null;
                        setPresetName(preset?.label || '');
                        setValueMaps(preset?.valueMaps || {});
                        setMapping(preset?.mappingSrcToCanon || {});
                      }}
                      className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    >
                      <option value="" disabled>— select a schema —</option>
                      {filteredPresets.map(p => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                      </svg>
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">
                    ZIP uploads don’t support manual mapping. Pick a preset to tag the job.
                  </p>
                  {/* Show headers guidance for the chosen preset (ZIP only; no status banner) */}
                  {presetId && (
                    (() => {
                      const selectedZipPreset = filteredPresets.find(p => p.id === presetId) || null;
                      if (!selectedZipPreset) return null;
                      const { expected, mustHave } = computeZipPresetHints(selectedZipPreset, requiredCanon);

                      return (
                        <div className="mt-2 space-y-1 text-[11px]">
                          <p className="text-gray-700">
                            <span className="font-medium">Expected headers for</span>{" "}
                            <span className="font-medium">{selectedZipPreset.label}</span>:{" "}
                            {expected.length ? expected.join(", ") : "—"}
                          </p>
                          <p className="text-gray-700">
                            Must-have headers (map to required canon):{" "}
                            {mustHave.length ? mustHave.join(", ") : "none"}
                          </p>
                        </div>
                      );
                    })()
                  )}

                </div>

              )}

            </div>
          )}


          {/* Platform -> then schema presets (refined) */}
          {headerPreview && !isZip(file) && (
            <div className="rounded-xl border border-gray-200 bg-white/90 shadow-sm">
              {/* top accent */}
              <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-purple-500 to-orange-500 rounded-t-xl" />

              <div className="p-5 md:p-6">
                {/* Platform FIRST */}
                <label className="block text-xs text-gray-700 mb-1.5">
                  Platform <span className="text-gray-500">(affects how data is processed)</span>
                </label>

                {/* pretty select with icon + chevron */}
                <div className="relative mb-4">
                  <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center">
                    {/* platform icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h10M4 18h7" />
                    </svg>
                  </span>
                  <select
                    value={platform}
                    onChange={(e) => {
                      const next = e.target.value;
                      setPlatform(next);
                      setPresetId('');
                      setPresetName('');
                      setMapping(null);
                      setFileError('');
                    }}
                    className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-8 pr-8 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-orange-500"
                  >
                    <option value="" disabled>— select a platform —</option>
                     {isDeeper ? (
                      <>
                        <option value="x">X (Twitter)</option>
                        <option value="youtube-vid-text">Youtube Videos Texts</option>
                        <option value="facebook">Facebook</option>
                      </>
                    ) : (
                      <>
                        <option value="x">X (Twitter)</option>
                        <option value="reddit">Reddit</option>
                        <option value="steam">Steam</option>
                        <option value="youtube-vid-text">Youtube Videos Texts</option>
                        <option value="youtube-comments">Youtube Comments </option>
                        <option value="disqus">Disqus</option>
                        <option value="bluesky">Bluesky</option>
                        <option value="facebook">Facebook</option>
                      </>
                    )}
                    {/* <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="youtube">YouTube</option> */}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                    {/* chevron */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                    </svg>
                  </span>
                </div>

                {/* Only show schema presets after platform is chosen */}
                {platform && (
                  <div className="mt-4">
                    <label className="block text-xs text-gray-700 mb-1.5">
                      Schema preset
                    </label>

                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center">
                        {/* layers icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3l8 4.5-8 4.5-8-4.5L12 3zm0 9l8 4.5-8 4.5-8-4.5L12 12z" />
                        </svg>
                      </span>

                      <select
                        value={presetId}
                        onChange={async (e) => {
                          const id = e.target.value;
                          setPresetId(id);
                          setFileError('');
                          if (!file || !headerPreview) return;

                          if (id === '') return;       // “select a schema”
                          if (id === 'manual') return;  // manual mode shows mapper below

                          const preset = filteredPresets.find(p => p.id === id) || null;
                          if (!preset) return;
                          setPresetName(preset.label);

                          await applyPresetIfPossible(preset, file, headerPreview);
                          setValueMaps(preset.valueMaps || {});
                        }}
                        className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-8 pr-8 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      >
                        <option value="" disabled>— select a schema —</option>
                        <option value="manual">No preset — map manually</option>
                        {filteredPresets.map(p => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>

                      <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                        {/* chevron */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                        </svg>
                      </span>
                    </div>

                    <p className="text-[11px] text-gray-500 mt-2">
                      Pick a known dataset to auto-map. Choose <span className="font-medium">“No preset — map manually”</span> to map yourself.
                    </p>
                    <div className="mt-2 space-y-1 text-[11px]">
                    {selectedPreset ? (
                      <>
                        {/* Success / failure banner */}
                        {isPresetMappedOK ? (
                          <p className="text-green-700">
                            ✅ <span className="font-medium">Mapped correctly</span> — all required fields are covered.
                          </p>
                        ) : presetRequiredSources.length > 0 ? (
                          <p className="text-red-600">
                            ⚠️ Still required: {presetRequiredSources.join(', ')}
                          </p>
                        ) : (
                          <p className="text-gray-600">
                            Select a file or complete mapping to continue.
                          </p>
                        )}

                        {/* What headers the user should have in their upload (from preset) */}
                        <p className="text-gray-700">
                          <span className="font-medium">Expected headers for</span>{" "}
                          <span className="font-medium">{selectedPreset.label}</span>:{" "}
                          {presetExpectedSources.length ? presetExpectedSources.join(", ") : "—"}
                        </p>

                        <p className={presetRequiredSources.length ? "text-gray-700" : "text-gray-500"}>
                          Must-have headers (map to required canon):{" "}
                          {presetRequiredSources.length ? presetRequiredSources.join(", ") : "none"}
                        </p>

                        {/* Optional: show what actually mapped */}
                        {mapping && Object.keys(mapping).length > 0 && (
                          <div className="text-gray-500">
                            {Object.entries(mapping).map(([src, canon]) => (
                              <span key={src} className="inline-block mr-2 mb-1">
                                {src} → <span className="font-medium">{canon}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-600">
                        Required fields for <span className="font-medium">{PLATFORM_SPECS[platform]?.label || platform}</span>: {requiredCanon.join(", ")}
                      </p>
                    )}
                  </div>
                  </div>
                )}
              </div>
            </div>
          )}


          {/*Manual Header mapping: show only when manual + platform selected*/}
          {headerPreview && platform && presetId === 'manual' && (
            <HeaderMapper
              preview={headerPreview}
              requiredKeys={requiredCanon}
              optionalKeys={optionalCanon}
              onConfirm={async (srcToCanon, options) => {
                const controller = new AbortController();
                try {
                  setLoading(true);
                  setProgress({ phase: 'validate', rows: 0 });
                  await canonicalizeFileStreaming(
                    file,
                    srcToCanon,
                    [],                             // no required fields enforcement
                    options?.valueMaps,             // ok to pass through; unused in Pass A
                    { onProgress: (p) => setProgress(p), signal: controller.signal }
                  );
                  setFileError('');
                } catch (e) {
                  setFileError(e?.message || String(e));
                  throw e; // if upstream UI expects thrown error
                } finally {
                  setProgress(null);
                  setLoading(false);
                }

                setMapping(srcToCanon);
                setValueMaps(options?.valueMaps || {});
                setFileError('');
              }}
            />
          )}

          {progress && (
            <div className="text-xs text-gray-600 mt-2">
              Processing ({progress.phase})… rows: {progress.rows.toLocaleString()}
            </div>
          )}

          {/* Pipeline Configuration (refined) */}
          <div className="space-y-5">
  {/* Row 1: Stem + Pull Date */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Stem */}
    <div>
      <label className="block text-xs text-gray-700 mb-1.5">
        Stem <InfoTooltip text="One-word description of topic" />
      </label>
      <div className="relative rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-orange-500">
        <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
          {/* tag icon */}
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M3 7a4 4 0 014-4h6.586a2 2 0 011.414.586l4.414 4.414a2 2 0 010 2.828L12 22l-9-9 0 0A4 4 0 013 7z" />
          </svg>
        </span>
        <input
          type="text"
          value={stem}
          onChange={(e) => setStem(e.target.value.slice(0, 30))}
          placeholder="Enter stem value"
          className="w-full rounded-lg pl-8 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none"
        />
      </div>
      {stemError && <p className="mt-1 text-xs text-red-600">{stemError}</p>}
    </div>

    {/* Pull Date */}
    <div>
      <label className="block text-xs text-gray-700 mb-1.5">
        Pull Date (MMDDYY) <InfoTooltip text="Time period (No spaces)" />
      </label>
      <div className="relative rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-orange-500">
        <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
          {/* calendar icon */}
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </span>
        <input
          type="text"
          value={pullDate}
          onChange={(e) => setPullDate(e.target.value.slice(0, 15))}
          placeholder="011525"
          className="w-full rounded-lg pl-8 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none"
          inputMode="numeric"
        />
      </div>
      {pullDateError && <p className="mt-1 text-xs text-red-600">{pullDateError}</p>}
    </div>
    </div>

    {/* Row 2: Users + Posts + Percentile + Cluster Threshold – hidden for deeper analysis */}
    {!isDeeper && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Minimum No. of Users */}
        <div>
          <label className="block text-xs text-gray-700 mb-1.5">
            Minimum No. of Users <InfoTooltip text="Minimum number of users who need to share an OI for it to be included in the analysis" />
          </label>
          <div className="relative rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-orange-500">
            <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
              {/* users icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M13 7a4 4 0 11-8 0 4 4 0 018 0zM21 20v-2a3 3 0 00-2-2.82M16 3.13a4 4 0 010 7.75" />
              </svg>
            </span>
            <input
              type="number"
              value={minSizeDay}
              min={0}
              onChange={(e) => setMinSizeDay(parseInt(e.target.value || '0', 10))}
              className="w-full rounded-lg pl-8 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none"
            />
          </div>
        </div>

        {/* Minimum No. of Posts */}
        <div>
          <label className="block text-xs text-gray-700 mb-1.5">
            Minimum No. of Posts <InfoTooltip text="Minimum number of messages that share an OI for it to be included in the analysis" />
          </label>
          <div className="relative rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-orange-500">
            <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
              {/* posts icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h10M4 18h7" />
              </svg>
            </span>
            <input
              type="number"
              value={minUserDay}
              min={0}
              onChange={(e) => setMinUserDay(parseInt(e.target.value || '0', 10))}
              className="w-full rounded-lg pl-8 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none"
            />
          </div>
        </div>

        {/* Percentile */}
        <div>
          <label className="block text-xs text-gray-700 mb-1.5">
            Percentile <InfoTooltip text="Percentile of extremism at which an OI is considered suspicious" />
          </label>
          <div className="relative rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-orange-500">
            <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
              {/* gauge icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3a9 9 0 019 9h-3a6 6 0 10-12 0H3a9 9 0 019-9zm0 9l4 4" />
              </svg>
            </span>
            <input
              type="number"
              value={percentile}
              min={0}
              max={100}
              step="1"
              onChange={(e) => {
                const value = e.target.value;

                // allow clearing the field
                if (value === "") {
                  setPercentile("");
                  return;
                }

                const num = Math.max(0, Math.min(100, Number(value)));
                setPercentile(num);
              }}
              onBlur={() => {
                // optional: normalize empty to 0 when user leaves the field
                if (percentile === "") {
                  setPercentile(0);
                }
              }}
              className="w-full rounded-lg pl-8 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none"
            />

          </div>
        </div>

        {/* Cluster Threshold */}
        <div>
          <label className="block text-xs text-gray-700 mb-1.5">
            Cluster Threshold <InfoTooltip text="Threshold for clustering analysis (0.0 - 1.0)" />
          </label>
          <div className="relative rounded-lg border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-orange-500">
            <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
              {/* cluster icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </span>
            <input
              type="number"
              value={clusterThreshold}
              min={0}
              max={1}
              step="0.1"
              onChange={(e) => {
                const value = e.target.value;

                // allow clearing the field
                if (value === "") {
                  setClusterThreshold("");
                  return;
                }

                const num = Math.max(0, Math.min(1, Number(value)));
                setClusterThreshold(num);
              }}
              onBlur={() => {
                // optional: normalize empty to 0.0 when user leaves the field
                if (clusterThreshold === "") {
                  setClusterThreshold(0.0);
                }
              }}
              className="w-full rounded-lg pl-8 pr-3 py-2 text-sm text-gray-900 bg-transparent outline-none"
            />

          </div>
        </div>
      </div>
    )}

    <p className="text-[11px] text-gray-500">
      Tip: Use a short, unique <span className="font-medium text-gray-700">Stem</span> (e.g., <span className="font-mono">election2024</span>) and a compact <span className="font-medium text-gray-700">Pull Date</span> like <span className="font-mono">011525</span>.
    </p>
  </div>




          {/* Submit */}
          <div className="flex flex-col gap-2 pt-4">
            {/* existing inline errors */}
            {mustMap && !mappingCoversRequired && (
              <span className="text-xs text-red-600">
                Select a Schema or manually map all required fields and click confirm to continue.
              </span>
            )}

            {fileError && <p className="mt-3 rounded-md border border-red-300 bg-red-100 px-3 py-2 text-[13px] text-red-800 font-semibold">{fileError}</p>}
            {stemError && <p className="text-red-600 text-xs">{stemError}</p>}
            {platformError && <p className="text-red-600 text-xs">{platformError}</p>}
            {pullDateError && <p className="text-red-600 text-xs">{pullDateError}</p>}

            {/* submit button */}
            <div className="flex justify-end mt-2">
              <button
                onClick={(e) => handleSubmit(e, isDeeper)}
                disabled={!canSubmit}
                className={`font-medium py-3 px-8 rounded-md transition-colors shadow-sm ${
                  canSubmit
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                }`}
              >
                {isDeeper ? 'Submit for deeper analysis' : 'Submit Parameters'}
              </button>
            </div>
          </div>


        </div>

        {/* Results */}
        {submitted && (
          <div className="mt-10 rounded-xl border border-gray-200 bg-white/90 shadow-sm">
            {/* top accent */}
            <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-purple-500 to-orange-500 rounded-t-xl" />

            <div className="p-6">
              <h2 className="text-xl md:text-2xl font-semibold text-purple-900 mb-6">
                Submitted Parameters
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                {/* left column */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 15c2.21 0 4.307.523 6.121 1.454M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Name
                    </span>
                    <span className="text-gray-900">{user.name || 'Not provided'}</span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l9 6 9-6M21 8v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8" />
                      </svg>
                      Email
                    </span>
                    <span className="text-gray-900">{user.email || 'Not provided'}</span>
                  </div>

                  {/* File row (wraps instead of truncating) */}
                  <div className="flex items-start justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      File
                    </span>
                    <span className="text-gray-900 text-right break-words max-w-[220px]">
                      {userfile ? userfile.name : 'None'}
                    </span>
                  </div>

                  {/* Stem row (tag icon) */}
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M7 7h.01M3 7a4 4 0 014-4h6.586a2 2 0 011.414.586l4.414 4.414a2 2 0 010 2.828L12 22l-9-9A4 4 0 013 7z"
                        />
                      </svg>
                      Stem
                    </span>
                    <span className="text-gray-900">{submittedData?.stem || 'Not set'}</span>
                  </div>


                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2 font-medium text-gray-700">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Pull Date
                    </span>
                    <span className="text-gray-900">{submittedData?.pullDate || 'Not set'}</span>
                  </div>
                </div>

                {/* right column */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="font-medium text-gray-700 flex items-center gap-2">
                      Platform
                    </span>
                    <span className="text-gray-900 capitalize">{submittedData?.platform}</span>
                  </div>
                  {/* Hide these 4 only for deeper */}
                  {!submittedIsDeeper && (
                    <>

                      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                        <span className="font-medium text-gray-700 flex items-center gap-2">
                          Min Size/Day
                        </span>
                        <span className="text-gray-900">{submittedData?.minSizeDay}</span>
                      </div>

                      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                        <span className="font-medium text-gray-700 flex items-center gap-2">
                          Min Users/Day
                        </span>
                        <span className="text-gray-900">{submittedData?.minUserDay}</span>
                      </div>

                      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                        <span className="font-medium text-gray-700 flex items-center gap-2">
                          Percentile
                        </span>
                        <span className="text-gray-900">{submittedData?.percentile}%</span>
                      </div>

                      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                        <span className="font-medium text-gray-700 flex items-center gap-2">
                          Cluster Threshold
                        </span>
                        <span className="text-gray-900">{submittedData?.clusterThreshold}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {showPopup && (
        <div className="fixed top-5 right-5 bg-green-100 border border-green-400 text-green-800 px-6 py-4 rounded shadow-lg z-50">
          <strong className="block font-medium">Thank you!</strong>
          <span>You will be notified on email when the reports are ready. To check the status or download the report look into Past Submissions tab. (Expected: time 15min)</span>
        </div>
      )}
      {loading && (
          <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center z-50 space-y-4">

          {/* Spinner */}
          <div className="h-14 w-14 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />

          {/* Message */}
          <p className="text-gray-700 text-lg font-medium tracking-wide">
            {loadingMessage || 'Processing…'}
          </p>

        </div>
      )}
    </div>
  );
}
